import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import { EditActions, editActions } from "../../actions/editActions";
import { hasWriteAccess, nameofFactory } from "../../utils/utils";
import Editor from "../../components/Editing/Editor";
import NumberField from "../../components/Editing/NumberField";
import TextField from "../../components/Editing/TextField";
import { Divider, Modal } from "semantic-ui-react";
import DropdownField from "../../components/Editing/DropdownField";
import { getEscalatorRTDGroupNames } from "../../utils/pdApiUtils";
import CheckboxField from "../../components/Editing/CheckboxField";
import ButtonField from "../../components/Editing/ButtonField";
import { EditLocalizationStatus } from "./EditLocalizationStatus";
import TextIndicatorField from "../../components/Editing/TextIndicatorField";

type StateProps = {
  editedEscalatorRTD: Api.EscalatorRTD;
  storedEscalatorRTD: Api.EscalatorRTD;
  pdApi: State.PdApi;
  activeUser?: Api.User;
  columns: State.ColumnMap;
  isShortenTextEnabled: boolean;
};

type DispatchProps = {
  editActions: typeof editActions;
};

type OwnProps = {
  toggleModal: () => void;
};

export type EditEscalatorRTDProps = StateProps & DispatchProps & OwnProps;

export type EditEscalatorRTDState = {
  isEditLocalizationStatusOpen: boolean;
  locsOriginal: Api.LocsEscalatorRTD[];
  locsEdited: Api.LocsEscalatorRTD[];
};

const escalatorRTDFields = nameofFactory<Api.EscalatorRTD>();

export class EditEscalatorRTD extends React.Component<
  EditEscalatorRTDProps,
  EditEscalatorRTDState
> {
  constructor(props: EditEscalatorRTDProps) {
    super(props);
    this.state = {
      isEditLocalizationStatusOpen: false,
      locsOriginal: [],
      locsEdited: []
    };
  }
  public render(): JSX.Element {
    const { isEditLocalizationStatusOpen } = this.state;
    const {
      editedEscalatorRTD,
      storedEscalatorRTD,
      activeUser,
      columns,
      pdApi,
      toggleModal,
      isShortenTextEnabled
    } = this.props;

    const isNew = !storedEscalatorRTD;
    const escalatorRTDGroupOptions = getEscalatorRTDGroupNames(pdApi);
    const permissions = hasWriteAccess(activeUser, "Escalator");

    return (
      <Editor
        itemName={"escalator rtd"}
        originalObject={storedEscalatorRTD}
        editedObject={editedEscalatorRTD}
        hasWritePermission={hasWriteAccess(activeUser, "Escalator")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTD,
            locs: this.state.locsEdited
          });
          this.props.editActions.update("escalatorRTDs");
        }}
        create={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTD,
            locs: this.state.locsEdited
          });
          this.props.editActions.add("escalatorRTDs");
        }}
        copy={() => {
          this.props.editActions.setEdited({
            ...editedEscalatorRTD,
            locs: this.state.locsEdited
          });
          this.props.editActions.startEditing(editedEscalatorRTD, true);
        }}
        remove={{ callback: toggleModal }}
      >
        <NumberField
          id="code"
          label="Code"
          value={editedEscalatorRTD.code}
          isError={!this.isFieldValid(escalatorRTDFields("code"))}
          errorMessage="The code must be unique."
          readonly={!isNew}
          onChange={code => this.props.editActions.setEdited({ ...editedEscalatorRTD, code })}
        />
        <DropdownField
          id="escalatorRTDGroup"
          label="RTD Group"
          value={editedEscalatorRTD.rtdGroupEnumValue}
          options={escalatorRTDGroupOptions}
          search={true}
          onChange={rtdGroupEnumValueIndex => {
            const selected = pdApi.escalatorRTDGroups.find(
              i => i.enumValue === rtdGroupEnumValueIndex
            );
            if (selected) {
              this.props.editActions.setEdited({
                ...editedEscalatorRTD,
                rtdGroupEnumValue: selected.enumValue
              });
            }
          }}
          disabled={!permissions}
        />
        <TextField
          id="swName"
          label="Sw Name"
          value={editedEscalatorRTD.swName}
          isError={!this.isFieldValid(escalatorRTDFields("swName"))}
          errorMessage={
            !this.isEmptyString(editedEscalatorRTD.swName) ? "The Sw name must be unique." : ""
          }
          onChange={swName =>
            this.props.editActions.setEdited({ ...editedEscalatorRTD, swName: swName.trim() })
          }
          readonly={!permissions}
        />
        <Divider horizontal={true}>{"English translations"}</Divider>
        <TextIndicatorField
          id={"needsUpdateIndicator"}
          text={"Edited texts will be translated."}
          isWarning={this.englishTranslationsChanged()}
        />
        <TextField
          id="name"
          label="English Name"
          value={editedEscalatorRTD.name}
          isError={!this.isFieldValid(escalatorRTDFields("name"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedEscalatorRTD,
              name: value.trim()
            } as Api.EscalatorRTD);
          }}
          readonly={!permissions}
        />
        <TextField
          id="description"
          label="Description"
          value={editedEscalatorRTD.description}
          isError={!this.isFieldValid(escalatorRTDFields("description"))}
          onChange={value => {
            this.props.editActions.setEdited({
              ...editedEscalatorRTD,
              description: value.trim()
            } as Api.EscalatorRTD);
          }}
          readonly={!permissions}
        />
        <ButtonField
          id={"viewTranslationsButton"}
          label="View translations"
          onButtonClick={this.toggleEditLocalizationStatusModal}
        />
        <Divider horizontal={true}>{"RTD properties"}</Divider>
        <TextField
          id="unitName"
          label="Unit Name"
          value={editedEscalatorRTD.unitName}
          isError={!this.isFieldValid(escalatorRTDFields("unitName"))}
          onChange={unitName =>
            this.props.editActions.setEdited({ ...editedEscalatorRTD, unitName: unitName.trim() })
          }
          readonly={!permissions}
        />
        <CheckboxField
          id={"localizable"}
          label={"RTD is localizable"}
          checked={editedEscalatorRTD.localizable}
          onChange={value =>
            this.props.editActions.setEdited({ ...editedEscalatorRTD, localizable: value })
          }
          disabled={!permissions}
        />
        <Modal size="large" open={isEditLocalizationStatusOpen} className="data-sections-modal">
          <EditLocalizationStatus
            itemType={"Escalator RTD"}
            editedItem={{ ...editedEscalatorRTD, locs: this.state.locsEdited }}
            getOption="escalatorRTDLocalizations"
            columns={columns["Escalator RTD Localization Status"]}
            pdApi={pdApi}
            toggleEditLocalizationStatusModal={this.toggleEditLocalizationStatusModal}
            setLocalizationsEdited={this.setLocalizationsEdited}
            isShortenTextEnabled={isShortenTextEnabled}
            englishTranslationChanged={this.englishTranslationsChanged()}
            userPermissions={permissions}
            locsOriginal={this.state.locsOriginal || []}
            setLocalizationsNonEdited={this.setLocalizationsNonEdited}
          />
        </Modal>
      </Editor>
    );
  }

  private englishTranslationsChanged = (): boolean =>
    this.props.editedEscalatorRTD.name !== this.props.storedEscalatorRTD?.name ||
    this.props.editedEscalatorRTD.description !== this.props.storedEscalatorRTD?.description;

  private handleEnglishTranslationsChange(item: Api.EscalatorRTD) {
    const { storedEscalatorRTD, editedEscalatorRTD } = this.props;
    const { locsOriginal, locsEdited } = this.state;

    // create both edited and stored item locs if needed
    const edited =
      locsEdited.length === 0
        ? [
            {
              text: item.name,
              description: item.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEscalatorRTD
          ]
        : this.state.locsEdited.map(loc => {
            if (loc.localeNavigation.locale === "en-US") {
              return {
                ...loc,
                text: item.name,
                description: item.description
              } as Api.LocsEscalatorRTD;
            } else {
              return loc;
            }
          });
    const original =
      locsOriginal.length === 0
        ? [
            {
              text: storedEscalatorRTD?.name,
              description: storedEscalatorRTD?.description,
              localeNavigation: { locale: "en-US" }
            } as Api.LocsEscalatorRTD
          ]
        : locsOriginal;

    this.setState((prevState: EditEscalatorRTDState) => ({
      ...prevState,
      locsEdited: edited,
      locsOriginal: original
    }));
    this.props.editActions.setEdited({
      ...editedEscalatorRTD,
      name: item.name,
      description: item.description
    });
  }

  private toggleEditLocalizationStatusModal = (): void => {
    this.setState((prevState: EditEscalatorRTDState) => ({
      ...prevState,
      isEditLocalizationStatusOpen: !prevState.isEditLocalizationStatusOpen
    }));
  };

  private setLocalizationsEdited = (
    newLocalizations: Api.LocsEscalatorRTD[],
    oldLocalizations: Api.LocsEscalatorRTD[]
  ): void => {
    const { editedEscalatorRTD } = this.props;
    this.setState((prevState: EditEscalatorRTDState) => ({
      ...prevState,
      locsEdited: newLocalizations,
      locsOriginal: oldLocalizations
    }));
    this.props.editActions.setEdited({
      ...editedEscalatorRTD,
      name:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.text ||
        editedEscalatorRTD?.name,
      description:
        newLocalizations.find(loc => loc.localeNavigation.locale === "en-US")?.description ||
        editedEscalatorRTD?.description,
      locs: newLocalizations
    } as Api.EscalatorRTD);
  };

  private setLocalizationsNonEdited = (Localizations: Api.LocsEscalatorRTD[]): void => {
    this.setState((prevState: EditEscalatorRTDState) => ({
      ...prevState,
      locsOriginal: Localizations
    }));
  };
  private isFieldValid = (field: string): boolean => {
    const { editedEscalatorRTD, storedEscalatorRTD, pdApi } = this.props;
    if (!Number.isInteger(editedEscalatorRTD.code)) return false;
    const isNew = !storedEscalatorRTD;
    switch (field) {
      case escalatorRTDFields("code"):
        return _.values(pdApi.escalatorRTDs).find(
          escalatorRTD =>
            (isNew || escalatorRTD.id !== editedEscalatorRTD.id) &&
            escalatorRTD.code === editedEscalatorRTD.code
        )
          ? false
          : true;
      case escalatorRTDFields("swName"): {
        const { swName } = editedEscalatorRTD;
        if (this.isEmptyString(swName)) {
          return false;
        } else {
          return _.values(pdApi.escalatorRTDs).find(
            escalatorRTD =>
              (isNew || escalatorRTD.id !== editedEscalatorRTD.id) &&
              escalatorRTD.swName?.trim().toLowerCase() ===
                editedEscalatorRTD.swName?.trim().toLowerCase()
          )
            ? false
            : true;
        }
      }
      case escalatorRTDFields("name"): {
        const { name } = editedEscalatorRTD;
        return !this.isEmptyString(name);
      }
      case escalatorRTDFields("description"): {
        const { description } = editedEscalatorRTD;
        return !this.isEmptyString(description);
      }
      default:
        return true;
    }
  };

  private isEmptyString = (str: string): boolean => !(!!str.trim() && str.trim().length > 0);

  private isAllFieldsValid = (): boolean => {
    const { editedEscalatorRTD } = this.props;
    const temp = _.keys(editedEscalatorRTD);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedEscalatorRTD: state.editView.editedObject,
  storedEscalatorRTD: state.editView.storedObject,
  pdApi: state.pdApi,
  activeUser: state.main.activeUser,
  isShortenTextEnabled: state.main.isShortenTextEnabled
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditEscalatorRTD);
